import React, { useState, useEffect, useMemo } from 'react';
import { Tabs, theme } from 'antd';
import classNames from 'classnames';
import { history, useLocation } from '@umijs/max';
import './index.less';

export interface HashTabItem {
  /** 显示文案 */
  label: React.ReactNode;
  /** 业务唯一 key */
  key: string;
  /** 路由路径（hash 或 path 段，如 list） */
  path?: string;
  /** 路由对应的组件 */
  element: React.ReactNode;
}

export interface HashTabsProps {
  /** 选项列表 */
  items: HashTabItem[];
  /** 自定义类名（可选） */
  className?: string;
  /** 内容区额外样式（可选） */
  contentStyle?: React.CSSProperties;
  /** 默认重定向到哪个 key（可选，默认取第一个） */
  defaultRedirect?: string;
  /** 设置后用浏览器 pathname 路由（如 /base/list），不再用 location.hash */
  basePath?: string;
}

function tabKeyFromSegment(
  segment: string,
  items: HashTabItem[],
  defaultRedirect?: string,
): string {
  if (!segment) return defaultRedirect || items[0]?.key;
  const match = items.find((it) => (it.path || it.key) === segment);
  return match?.key ?? (defaultRedirect || items[0]?.key);
}

const HashTabs: React.FC<HashTabsProps> = ({
  items,
  className,
  contentStyle,
  defaultRedirect,
  basePath,
}) => {
  const { token } = theme.useToken();
  const location = useLocation();
  const pathBase = basePath?.replace(/\/$/, '') || '';

  const getHashKey = (): string => {
    const hash = window.location.hash.replace(/^#/, '').replace(/^\//, '');
    if (!hash) {
      return defaultRedirect || items[0]?.key;
    }
    return tabKeyFromSegment(hash, items, defaultRedirect);
  };

  const getPathKey = (): string => {
    if (!pathBase || !location.pathname.startsWith(pathBase)) {
      return defaultRedirect || items[0]?.key;
    }
    const segment = location.pathname.slice(pathBase.length).replace(/^\//, '');
    return tabKeyFromSegment(segment, items, defaultRedirect);
  };

  const [activeKey, setActiveKey] = useState<string>(() => (pathBase ? getPathKey() : getHashKey()));

  useEffect(() => {
    if (pathBase) {
      setActiveKey(getPathKey());
      return undefined;
    }

    const handleHashChange = () => {
      setActiveKey(getHashKey());
    };

    window.addEventListener('hashchange', handleHashChange);
    if (!window.location.hash) {
      const firstKey = defaultRedirect || items[0]?.key;
      const firstItem = items.find((it) => it.key === firstKey) || items[0];
      const hashPath = firstItem?.path || firstItem?.key;
      if (hashPath) {
        const base = window.location.pathname + window.location.search;
        window.location.replace(`${base}#${hashPath}`);
        setActiveKey(firstItem.key);
      }
    } else {
      setActiveKey(getHashKey());
    }

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [items, defaultRedirect, pathBase, location.pathname]);

  useEffect(() => {
    if (!pathBase) return;
    if (location.pathname === pathBase || location.pathname === `${pathBase}/`) {
      const firstKey = defaultRedirect || items[0]?.key;
      const firstItem = items.find((it) => it.key === firstKey) || items[0];
      const seg = firstItem?.path || firstItem?.key;
      history.replace(`${pathBase}/${seg}${location.search}`);
    }
  }, [pathBase, location.pathname, location.search, items, defaultRedirect]);

  const currentElement = useMemo(() => {
    const currentItem = items.find((it) => it.key === activeKey);
    return currentItem?.element ?? items[0]?.element;
  }, [items, activeKey]);

  const handleTabChange = (key: string) => {
    const target = items.find((it) => it.key === key);
    if (!target) return;
    const seg = target.path || target.key;
    if (pathBase) {
      history.push(`${pathBase}/${seg}`);
      setActiveKey(key);
      return;
    }
    window.location.hash = seg;
    setActiveKey(key);
  };

  return (
    <>
      <Tabs
        className={classNames('hash-tabs', className)}
        activeKey={activeKey}
        onChange={handleTabChange}
        items={items.map((it) => ({ key: it.key, label: it.label }))}
      />
      <div style={{ marginTop: token.margin, ...contentStyle }}>
        {currentElement}
      </div>
    </>
  );
};

export default HashTabs;
